import React, { useEffect, useMemo, useState } from "react";

import { Body1, Card, Spinner, Title1, tokens } from "@fluentui/react-components";
import { ChevronLeft16Filled, ChevronRight16Filled } from "@fluentui/react-icons";
import DOMPurify from "dompurify";
import { useLocation, useNavigate } from "react-router-dom";

import { useStyles } from "./styles";
import Button from "../../../../components/Button/Button";
import { AnalyzeResult, LawCase, ResolutionStatus } from "../../../../models/benchly.models";
import { benchlyService } from "../../../../service/benchly.service";
import { DocumentActionService } from "../../../../service/documentActions.service";
import { useCitationStore } from "../../../../store/CitationStore";

const ViewCases: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentReferenceId: initialDocumentReferenceId } =
    (location.state as { documentReferenceId?: string }) || {};

  const { citations, isLoading, loadCitationsFromStorage } = useCitationStore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [htmlCache, setHtmlCache] = useState<Map<string, string>>(new Map());
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const [htmlError, setHtmlError] = useState<string | null>(null);

  const getPrimaryLawCase = (r: AnalyzeResult | undefined): LawCase | null => {
    if (!r) return null;

    if (r.resolvedLawCase && r.resolvedLawCase !== "none") return r.resolvedLawCase;

    const cases = r.citations ?? [];
    return cases.find((c) => (c.foundDocs?.length ?? 0) > 0) ?? cases[0] ?? null;
  };

  const getDisplaySource = (r: AnalyzeResult | undefined) => {
    const lawCase = getPrimaryLawCase(r);
    return {
      lawCase,
      identifierOnDocument: lawCase?.identifierOnDocument ?? "",
      titleOnDocument: lawCase?.titleOnDocument,
    };
  };

  // Filter only Certified citations (at result-level using primary LawCase status)
  const certifiedCitations = useMemo(() => {
    return citations.filter((r) => {
      const lawCase = getPrimaryLawCase(r);
      return lawCase?.status === ResolutionStatus.ValidatedAndCertified;
    });
  }, [citations]);

  // Load citations from storage if not already loaded
  useEffect(() => {
    if (citations.length === 0 && !isLoading) {
      loadCitationsFromStorage();
    }
  }, [citations.length, isLoading, loadCitationsFromStorage]);

  // If navigated with a specific documentReferenceId, find its index
  useEffect(() => {
    if (initialDocumentReferenceId && certifiedCitations.length > 0) {
      const index = certifiedCitations.findIndex(
        (c) => c.documentReferenceId === initialDocumentReferenceId
      );
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [initialDocumentReferenceId, certifiedCitations]);

  // Select the citation in Word when navigating
  const currentCitation = certifiedCitations[currentIndex];

  useEffect(() => {
    if (currentCitation?.documentReferenceId) {
      DocumentActionService.selectCitation(currentCitation.documentReferenceId);
    }
  }, [currentCitation?.documentReferenceId]);

  // Automatically load HTML when citation changes
  useEffect(() => {
    const loadHtmlForCurrentCase = async () => {
      const primaryLawCase = getPrimaryLawCase(currentCitation);
      const docId = primaryLawCase?.foundDocs?.[0]?.docId;

      if (!docId) {
        setHtmlError(null);
        return;
      }

      // Check if HTML is already cached
      if (htmlCache.has(docId)) {
        setHtmlError(null);
        return;
      }

      // Load HTML from API
      setIsLoadingHtml(true);
      setHtmlError(null);

      try {
        const documentDetail = await benchlyService.getDocumentById(docId);
        if (documentDetail.Html) {
          setHtmlCache((prev) => new Map(prev).set(docId, documentDetail.Html));
        } else {
          setHtmlError("No HTML content available for this document.");
        }
      } catch (error) {
        console.error("Failed to fetch document HTML:", error);
        setHtmlError("Failed to load document content.");
      } finally {
        setIsLoadingHtml(false);
      }
    };

    loadHtmlForCurrentCase();
  }, [currentCitation, htmlCache]);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, certifiedCitations.length - 1));
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Display fields + primary matched doc
  const {
    lawCase: primaryLawCase,
    identifierOnDocument,
    titleOnDocument,
  } = getDisplaySource(currentCitation);

  const matchedCase = primaryLawCase?.foundDocs?.[0];
  const currentHtmlContent = matchedCase?.docId ? htmlCache.get(matchedCase.docId) : null;

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <Title1>View Cited Cases</Title1>
        <div className={styles.loadingContainer}>
          <Spinner size="medium" />
          <Body1>Loading citations...</Body1>
        </div>
      </div>
    );
  }

  // Show empty state
  if (certifiedCitations.length === 0) {
    return (
      <div className={styles.container}>
        <Title1>View Cited Cases</Title1>
        <Body1
          style={{ color: tokens.colorNeutralForeground2, fontStyle: "italic", marginTop: 20 }}
        >
          No certified citations found. Run a scan to find and verify citations.
        </Body1>
        <Button
          variant="primary"
          onClick={() => navigate("/")}
          size="small"
          style={{ marginTop: 16 }}
        >
          Go to Home
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Sticky header section */}
      <div className={styles.stickyHeader}>
        <Title1>View Cited Cases</Title1>

        {/* Navigation with position indicator */}
        <div className={styles.navigationContainer}>
          <Button
            variant="outline-primary"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            size="small"
            icon={<ChevronLeft16Filled />}
          >
            Back
          </Button>
          <Body1 className={styles.positionIndicator}>
            <strong>{currentIndex + 1}</strong> of <strong>{certifiedCitations.length}</strong>
          </Body1>
          <Button
            variant="outline-primary"
            onClick={handleNext}
            disabled={currentIndex === certifiedCitations.length - 1}
            size="small"
            iconPosition="after"
            icon={<ChevronRight16Filled />}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Scrollable content section */}
      <div className={styles.scrollableContent}>
        {currentCitation ? (
          <Card className={styles.caseCard}>
            {/* Citation from document */}
            <div
              style={{
                marginBottom: 16,
                paddingBottom: 16,
                borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
              }}
            >
              <Body1
                style={{ fontWeight: 700, fontSize: 18, color: tokens.colorNeutralForeground1 }}
              >
                {identifierOnDocument || "(missing identifier)"}
              </Body1>

              {titleOnDocument && (
                <Body1
                  style={{
                    fontStyle: "italic",
                    color: tokens.colorNeutralForeground2,
                    marginTop: 4,
                  }}
                >
                  {titleOnDocument}
                </Body1>
              )}
            </div>

            {/* Matched case details */}
            {matchedCase ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <Body1
                    style={{
                      fontWeight: 600,
                      color: tokens.colorNeutralForeground1,
                      marginBottom: 4,
                      paddingRight: 4,
                    }}
                  >
                    Matched Case
                  </Body1>
                  <Body1 style={{ fontWeight: 600, fontSize: 16 }}>{matchedCase.title}</Body1>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px" }}>
                  <Body1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground2 }}>
                    Identifier:
                  </Body1>
                  <Body1>{matchedCase.identifier}</Body1>

                  <Body1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground2 }}>
                    Court:
                  </Body1>
                  <Body1>{matchedCase.court || matchedCase.courtAbbreviation || "N/A"}</Body1>

                  <Body1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground2 }}>
                    Date:
                  </Body1>
                  <Body1>{formatDate(matchedCase.docDate)}</Body1>
                </div>
              </div>
            ) : (
              <Body1 style={{ color: tokens.colorNeutralForeground2, fontStyle: "italic" }}>
                No matched case details available.
              </Body1>
            )}

            {/* Full Case HTML Content Section */}
            <div
              style={{
                marginTop: 24,
                paddingTop: 24,
                borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
              }}
            >
              <Body1
                style={{ fontWeight: 600, color: tokens.colorNeutralForeground1, marginBottom: 12 }}
              >
                Full Case Content
              </Body1>

              {isLoadingHtml && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Spinner size="small" />
                  <Body1>Loading case content...</Body1>
                </div>
              )}

              {htmlError && (
                <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>{htmlError}</Body1>
              )}

              {currentHtmlContent && !isLoadingHtml && (
                <div
                  style={{
                    padding: 16,
                    border: `1px solid ${tokens.colorNeutralStroke1}`,
                    borderRadius: 4,
                    backgroundColor: tokens.colorNeutralBackground1,
                    maxHeight: "500px",
                    overflow: "auto",
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentHtmlContent) }}
                />
              )}
            </div>
          </Card>
        ) : (
          <div className={styles.loadingContainer}>
            <Spinner size="medium" />
            <Body1>Loading case...</Body1>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewCases;
